import { HOLDINGS_EVENT_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Textarea, Title } from '@mantine/core';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import {
	RegularViewTableItemData,
	SimpleRegularViewTableItemData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { useBulkHoldingEventDrawerController } from './useBulkHoldingEventDrawerController';
import { getDateLabel } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/utils.ts';
import {
	HoldingCard,
	HoldingsEventTab,
	HoldingsEventTabs,
	InfoCard,
	ParentDiv,
	SelectTitle,
	UnitsTitle,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled.tsx';
import { FilePreview } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/FilePreviewComponent.tsx';
import { PledgeAgainstSelectComponent as PledgedAgainstSelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/PledgeAgainstSelectComponent';
import { EntitySelectComponent as HoldingEntitySelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/EntitySelectComponent.tsx';
import { FileUploadComponent } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/FileUploadComponent.tsx';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import { HoldingEventType } from '@dealstack/shared/lib/generated';

interface Props {
	selectedRows: RegularViewTableItemData[];
	className?: string;
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	refreshSecuritiesList: () => void;
	debtHoldings?: SimpleRegularViewTableItemData[];
}

export const BulkCreateHoldingEventDrawer = ({
	selectedRows,
	opened,
	onClose,
	onSuccess,
	className,
	refreshSecuritiesList,
	debtHoldings = [],
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		bulkHoldingsEventForm,
		createBulkHoldingsEventRequest,
		isSubmitting,
		entitiesSelectOptions,
		onSubmit,
		resetForm,
		BULK_HOLDING_EVENT_FORM_FIELD,
		uploadedFile,
		setUploadedFile,
		removeFile,
	} = useBulkHoldingEventDrawerController(
		selectedRows,
		refreshSecuritiesList,
		onClose,
		onSuccess
	);

	const [localEntitiesOptions, setLocalEntitiesOptions] = useState(entitiesSelectOptions);

	useEffect(() => {
		setLocalEntitiesOptions(entitiesSelectOptions);
	}, [entitiesSelectOptions]);

	const selectedType = useWatch({
		control: bulkHoldingsEventForm.control,
		name: BULK_HOLDING_EVENT_FORM_FIELD.type,
	});

	const allEventTypesUsed = useMemo(() => {
		if (selectedRows.length === 0) {
			return false;
		}

		return HOLDINGS_EVENT_TYPE_OPTIONS.every((option) => {
			const eventType = option.value as HoldingEventType;
			return selectedRows.every((row) =>
				row.events?.some((event) => event.type === eventType)
			);
		});
	}, [selectedRows]);

	useEffect(() => {
		if (opened) {
			resetForm();
			const currentType = bulkHoldingsEventForm.getValues(BULK_HOLDING_EVENT_FORM_FIELD.type);
			if (currentType !== HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES) {
				bulkHoldingsEventForm.setValue(
					BULK_HOLDING_EVENT_FORM_FIELD.type,
					HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES
				);
			}
		}
	}, [opened]);

	const dateLabel = getDateLabel(selectedType, tCapManage);

	const handleTabSelect = (type: HoldingEventType) => {
		bulkHoldingsEventForm.setValue(BULK_HOLDING_EVENT_FORM_FIELD.type, type);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<Title order={4} fw={600}>
					{tCapManage('holdings.event.drawer_add')}
				</Title>
			}
		>
			{createBulkHoldingsEventRequest.isPending && <InlineLoader />}
			{!allEventTypesUsed && (
				<FormProvider {...bulkHoldingsEventForm}>
					<form
						onSubmit={bulkHoldingsEventForm.handleSubmit(onSubmit)}
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: '1rem',
						}}
					>
						<ParentDiv>
							<HoldingCard>
								<SelectTitle>
									{tCapManage('holdings.event.select_type')}
								</SelectTitle>
								<HoldingsEventTabs>
									{HOLDINGS_EVENT_TYPE_OPTIONS.map((option) => {
										const isEventTypeUsedForAny = selectedRows.some((row) =>
											row.events?.some((event) => event.type === option.value)
										);

										return (
											<HoldingsEventTab
												key={option.value}
												selected={selectedType === option.value}
												disabled={isEventTypeUsedForAny}
												onClick={() =>
													!isEventTypeUsedForAny &&
													handleTabSelect(option.value)
												}
											>
												{option.label}
											</HoldingsEventTab>
										);
									})}
								</HoldingsEventTabs>
							</HoldingCard>

							<InfoCard>
								<MantineDateInput
									name={BULK_HOLDING_EVENT_FORM_FIELD.date}
									label={dateLabel}
									placeholder={tCapManage('holdings.event.select_date')}
									size="md"
									required
									error={bulkHoldingsEventForm.formState.errors?.date?.message}
									onChange={(value) => {
										const isoDate = browserDateToMidnightUTC(value);
										if (!isoDate) {
											return;
										}

										bulkHoldingsEventForm.setValue(
											BULK_HOLDING_EVENT_FORM_FIELD.date,
											isoDate
										);
									}}
								/>

								{selectedType === HoldingEventType.PLEDGING_OF_SHARES && (
									<>
										<HoldingEntitySelect
											name={BULK_HOLDING_EVENT_FORM_FIELD.pledgeToString}
											personIdFieldName={
												BULK_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId
											}
											label={tCapManage('holdings.event.pledge_to')}
											placeholder={tCapManage('holdings.event.pledge_person')}
											options={localEntitiesOptions}
											setOptions={setLocalEntitiesOptions}
										/>
										<PledgedAgainstSelect
											debtHoldings={debtHoldings}
											formFields={{
												pledgeFromString:
													BULK_HOLDING_EVENT_FORM_FIELD.pledgeFromString,
												pledgeFromHolderId:
													BULK_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId,
												pledgeFromSecurityId:
													BULK_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId,
											}}
										/>
									</>
								)}

								{selectedType === HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS && (
									<HoldingEntitySelect
										name={BULK_HOLDING_EVENT_FORM_FIELD.assignedToString}
										personIdFieldName={
											BULK_HOLDING_EVENT_FORM_FIELD.assignedToPersonId
										}
										label={tCapManage('holdings.event.assigned_to')}
										placeholder={tCapManage('holdings.event.pledge_person')}
										options={localEntitiesOptions}
										setOptions={setLocalEntitiesOptions}
									/>
								)}

								<Textarea
									{...bulkHoldingsEventForm.register(
										BULK_HOLDING_EVENT_FORM_FIELD.note
									)}
									label="Note"
									placeholder="Enter a description..."
									size="md"
									autosize
									minRows={4}
								/>

								<UnitsTitle>{tCapManage('holdings.event.document')}</UnitsTitle>
								{!uploadedFile ? (
									<FileUploadComponent
										setUploadedFile={setUploadedFile}
										inputId="bulkFileUpload"
									/>
								) : (
									<FilePreview
										fileName={uploadedFile?.name || 'Holding file'}
										onRemove={removeFile}
									/>
								)}
							</InfoCard>
						</ParentDiv>

						<Button
							fullWidth
							mt="auto"
							type="submit"
							loading={isSubmitting}
							disabled={selectedRows.length === 0}
						>
							{tCapManage('holdings.event.create')}
						</Button>
						<Button fullWidth variant="default" onClick={() => onClose()}>
							{tCapManage('holdings.event.cancel')}
						</Button>
					</form>
				</FormProvider>
			)}
		</Drawer>
	);
};
